// V2 版式：timeline-v（前端 DOM 版）——对照后端 v2/layouts/timeline-v.ts 逐坐标移植
// 左侧 2/3：竖向轴线 + 节点方块（首节点 accent）+ 节点文案（title 粗 + desc 小字）；
// 右侧 1/3：可选 metric block 渲染巨号 KPI（fontHero · accent），无 metric 时留空。
// 供料 timeline block（+ 可选 1 个 metric block），timeline 缺省降级 bullets。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { isLuxe } from "./luxe";
import { brutalTheme, termTheme, memphisTheme, memphisPalette } from "./themes";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

// 与后端 timeline-v.ts GEO 同源（v2geo.generated.ts 由 deno dump 生成）
const GEO = V2GEO["timeline-v"] as any;

export default function V2TimelineV({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const m = SPACING.marginX;
  const W = 13.333;
  // luxe：节点改为裸金圆点（accent，全部节点统一——timeline-v.ts 分支）
  const luxe = isLuxe(skin);
  // brutal（043 §4.3）：粗线轴；term（§4.4）：$ 步骤行前缀；memphis（§4.7）：圆角节点 5 色轮用
  const brutalT = brutalTheme(skin);
  const termT = termTheme(skin);
  const mpT = memphisTheme(skin);
  // 前端 Block 类型尚未含 V2 block（slides.ts 待主代理同步），此处按后端 types.ts 结构取用
  const blocks = c.blocks as any[];
  const ti = blocks.findIndex((b) => b.type === "timeline");
  const bi = blocks.findIndex((b) => b.type === "bullets");
  const mi = blocks.findIndex((b) => b.type === "metric");
  const metric = mi >= 0 ? blocks[mi] as { label: string; value: string } : null;

  let items: { title: string; desc?: string }[];
  if (ti >= 0) {
    items = blocks[ti].items;
  } else {
    items = (bi >= 0 ? (blocks[bi].items as string[]) : []).map((t) => ({ title: t }));
  }
  const nodes = items.slice(0, GEO.maxNodes);
  const rowH = nodes.length > 0 ? (GEO.axisBottom - GEO.axisTop) / nodes.length : 0;
  const titlePath = (i: number) => (ti >= 0 ? `blocks.${ti}.items.${i}.title` : bi >= 0 ? `blocks.${bi}.items.${i}` : null);
  const descPath = (i: number) => (ti >= 0 ? `blocks.${ti}.items.${i}.desc` : null);

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "timeline-v")} page={page} total={total} foot={footOf()} />
      {/* 页面大标题（长度分档） */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(m), top: px(GEO.titleY), width: px(W - 2 * m), height: px(GEO.titleH),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: skin.serifHead ? 700 : 400, lineHeight: 1.1,
        }}
      />
      {nodes.length > 0 && (
        <>
          {/* 左侧竖向轴线（brutal 下走 ruleW=3 粗黑线轴——timeline-v.ts:56-60） */}
          <div style={{
            position: "absolute", left: px(GEO.axisX), top: px(GEO.axisTop),
            width: brutalT ? px(SPACING.hairline * 3) : 1, height: px(GEO.axisBottom - GEO.axisTop), background: skin.hairline,
          }} />
          {nodes.map((it, i) => {
            const rowY = GEO.axisTop + rowH * i;
            const cy = rowY + rowH / 2;
            const tp = titlePath(i), dp = descPath(i);
            // 有 desc：title 贴节点上方、desc 在节点下方；无 desc：title 垂直居中（与后端一致）
            const ty = it.desc || (editable && dp) ? cy - GEO.nodeTitleH - 0.05 : cy - GEO.nodeTitleH / 2;
            return (
              <div key={i}>
                {/* 节点方块：首节点 accent，其余墨色（静态装饰；luxe 全部裸金圆点；memphis 圆角节点 5 色轮用） */}
                <div style={{
                  position: "absolute",
                  left: px(GEO.axisX + SPACING.hairline / 2 - GEO.node / 2), top: px(cy - GEO.node / 2),
                  width: px(GEO.node), height: px(GEO.node),
                  background: mpT
                    ? memphisPalette(mpT)[(page - 1 + i) % 5]
                    : luxe || i === 0 ? skin.accent : skin.text,
                  borderRadius: luxe ? "50%" : mpT ? px(0.08) : 0,
                }} />
                {/* 节点 title（粗体） */}
                {tp ? (
                  <EditableText
                    value={(termT ? "$ " : "") + it.title} path={tp} editable={editable}
                    // 045 #11：term `$ ` 前缀在持久化前剥离，防前缀存进文本叠加
                    onEdit={(p, v) => handlers.onEdit?.(p, termT && v.startsWith("$ ") ? v.slice(2) : v)}
                    onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                    style={{
                      position: "absolute", left: px(GEO.textX), top: px(ty),
                      width: px(GEO.textW), height: px(GEO.nodeTitleH),
                      fontSize: pt(skin.scale.body), color: skin.text, fontFamily: skin.fontBody,
                      fontWeight: 700, display: "flex", alignItems: "center",
                    }}
                  />
                ) : (
                  <div style={{
                    position: "absolute", left: px(GEO.textX), top: px(ty),
                    width: px(GEO.textW), height: px(GEO.nodeTitleH),
                    fontSize: pt(skin.scale.body), color: skin.text, fontFamily: skin.fontBody,
                    fontWeight: 700, display: "flex", alignItems: "center",
                  }}>{(termT ? "$ " : "") + it.title}</div>
                )}
                {/* 节点 desc（小字，timeline 供料才可编辑） */}
                {(it.desc || (editable && dp)) && (
                  dp ? (
                    <EditableText
                      value={it.desc ?? ""} path={dp} editable={editable} multiline
                      onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                      style={{
                        position: "absolute", left: px(GEO.textX), top: px(cy + 0.05),
                        width: px(GEO.textW), height: px(rowH / 2),
                        fontSize: pt(skin.scale.caption), color: skin.sub, fontFamily: skin.fontBody,
                        lineHeight: 1.15,
                      }}
                    />
                  ) : (
                    <div style={{
                      position: "absolute", left: px(GEO.textX), top: px(cy + 0.05),
                      width: px(GEO.textW), height: px(rowH / 2),
                      fontSize: pt(skin.scale.caption), color: skin.sub, fontFamily: skin.fontBody,
                      lineHeight: 1.15,
                    }}>{it.desc}</div>
                  )
                )}
              </div>
            );
          })}
        </>
      )}
      {/* 右侧 KPI（可选 metric block；有 metric 时才画分区 hairline，与后端一致） */}
      {metric && (
        <>
          <div style={{
            position: "absolute", left: px(GEO.splitX), top: px(GEO.axisTop),
            width: 1, height: px(GEO.axisBottom - GEO.axisTop), background: skin.hairline,
          }} />
          <EditableText
            value={metric.value} path={`blocks.${mi}.value`} editable={editable}
            onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
            style={{
              position: "absolute", left: px(GEO.kpiX), top: px(GEO.kpiY),
              width: px(GEO.kpiW), height: px(GEO.kpiH),
              fontSize: pt(skin.scale.dataHero), color: skin.accent, fontFamily: skin.fontHero,
              display: "flex", alignItems: "center",
            }}
          />
          <EditableText
            value={metric.label} path={`blocks.${mi}.label`} editable={editable} multiline
            onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
            style={{
              position: "absolute", left: px(GEO.kpiX), top: px(GEO.kpiLabelY),
              width: px(GEO.kpiW), height: px(GEO.kpiLabelH),
              fontSize: pt(skin.scale.body), color: skin.sub, fontFamily: skin.fontBody,
              lineHeight: 1.15,
            }}
          />
        </>
      )}
    </>
  );
}
